import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  Fingerprint,
  KeyRound,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  WifiOff,
  XCircle,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui/Skeleton'
import { formatDateTime } from '@/lib/format'
import {
  listChallenges,
  type ChallengeHold,
  type ChallengeStatus,
  type ChallengeType,
  type ChallengesListResponse,
} from '@/features/admin/challenges/api'

interface Props {
  /** Alert public UUID — the :id param on /alerts/:id. */
  alertId: string
  /** True when the alert produced a hold/block on the underlying transaction.
   *  Drives the "Action taken" badge when no challenge was raised. */
  isBlocking?: boolean
}

const STATUS: Record<ChallengeStatus, {
  label: string
  variant: 'success' | 'failed' | 'pending' | 'cancelled' | 'info' | 'default'
  Icon: typeof Clock
}> = {
  issued:        { label: 'Issued',        variant: 'pending',   Icon: Clock },
  delivered:     { label: 'Delivered',     variant: 'info',      Icon: ShieldCheck },
  passed:        { label: 'Passed',        variant: 'success',   Icon: CheckCircle2 },
  failed:        { label: 'Failed',        variant: 'failed',    Icon: XCircle },
  expired:       { label: 'Expired',       variant: 'cancelled', Icon: Clock },
  undeliverable: { label: 'Undeliverable', variant: 'failed',    Icon: WifiOff },
}

const TYPE_META: Record<ChallengeType, {
  label: string
  blurb: string
  Icon: typeof KeyRound
}> = {
  otp: {
    label: 'OTP step-up',
    blurb:
      'Customer entered a one-time code over the originating channel (SMS for USSD, inline for internet banking, push prompt for mobile).',
    Icon: KeyRound,
  },
  liveness: {
    label: 'Liveness check',
    blurb:
      'Customer took a selfie via the bank app SDK; the active vendor (Dojah / Smile Identity / Premblly) verified face match + liveness.',
    Icon: Fingerprint,
  },
  in_app_approval: {
    label: 'In-app approval',
    blurb:
      'Trusted device tapped Approve in the bank app; signed JWS verified against the device key.',
    Icon: Smartphone,
  },
}

/**
 * Triggered-action panel for /alerts/:id.
 *
 * Surfaces what the engine actually *did* on the alert's evaluation —
 * the step-up challenge (OTP / liveness / in-app approval) raised
 * via the realtime customer challenge plane, with full lifecycle
 * detail (status, attempts, vendor evidence, fail reason). Falls
 * back to "Auto-block" / "Alert + hold" / "Alert raised" badges when
 * no challenge was spawned, so the panel always tells the analyst
 * what happened, not just what could have happened.
 */
export function TriggeredActionPanel({ alertId, isBlocking }: Props) {
  const q = useQuery<ChallengesListResponse>({
    queryKey: ['alerts', alertId, 'challenges'],
    queryFn: () =>
      listChallenges({
        alertID: alertId,
        // The alert's decision fires once; widen the window so we
        // catch any retry holds (the verifier may issue a second
        // challenge if the first goes undeliverable).
        sinceMinutes: 60 * 24 * 30,
        limit: 25,
      }),
    enabled: !!alertId,
    staleTime: 15_000,
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert size={14} className="text-primary" aria-hidden />
          Action taken
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {q.isLoading ? (
          <Skeleton className="h-20" />
        ) : q.isError ? (
          <p className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            Couldn't load action details for this alert.
          </p>
        ) : q.data && q.data.holds.length > 0 ? (
          <ChallengeList holds={q.data.holds} />
        ) : (
          <FallbackAction isBlocking={isBlocking} />
        )}
      </CardContent>
    </Card>
  )
}

function ChallengeList({ holds }: { holds: ChallengeHold[] }) {
  return (
    <ul className="space-y-3">
      {holds.map((h) => (
        <li key={h.challenge_id}>
          <ChallengeCard hold={h} />
        </li>
      ))}
    </ul>
  )
}

function ChallengeCard({ hold }: { hold: ChallengeHold }) {
  const status = STATUS[hold.status]
  const meta = TYPE_META[hold.type] ?? {
    label: hold.type,
    blurb: '',
    Icon: ShieldCheck,
  }
  const StatusIcon = status.Icon
  const TypeIcon = meta.Icon

  return (
    <div className="rounded-md border border-border bg-card">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/20 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <TypeIcon size={14} className="text-primary" aria-hidden />
          <span className="text-sm font-semibold">{meta.label}</span>
          <Badge variant={status.variant}>
            <StatusIcon size={11} aria-hidden />
            {status.label}
          </Badge>
        </div>
        <code className="font-mono text-[0.6875rem] text-muted-fg">
          {hold.challenge_id.slice(0, 8)}
        </code>
      </header>

      <div className="space-y-3 px-4 py-3">
        {meta.blurb ? (
          <p className="text-xs text-muted-fg">{meta.blurb}</p>
        ) : null}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-4">
          <Field label="Channel">
            <code className="font-mono text-fg">{hold.channel}</code>
          </Field>
          <Field label="Delivery">
            <code className="font-mono text-fg">{hold.delivery}</code>
          </Field>
          <Field label="Attempts">
            <span className="font-mono tabular-nums text-fg">
              {hold.attempts}/{hold.max_attempts}
            </span>
          </Field>
          <Field label="Issued">
            <span className="text-fg" title={new Date(hold.created_at).toLocaleString()}>
              {formatRelative(hold.created_at)}
            </span>
          </Field>

          {hold.delivered_at ? (
            <Field label="Delivered">
              <span title={new Date(hold.delivered_at).toLocaleString()}>
                {formatRelative(hold.delivered_at)}
              </span>
            </Field>
          ) : null}
          {hold.verified_at ? (
            <Field label="Verified">
              <span title={new Date(hold.verified_at).toLocaleString()}>
                {formatRelative(hold.verified_at)}
              </span>
            </Field>
          ) : null}
          {!hold.verified_at && hold.status !== 'passed' && hold.status !== 'failed' ? (
            <Field label="Deadline">
              <span title={new Date(hold.expires_at).toLocaleString()}>
                {formatDateTime(hold.expires_at)}
              </span>
            </Field>
          ) : null}

          {hold.type === 'liveness' && hold.has_liveness_session ? (
            <Field label="Liveness session">
              <Badge variant="info" className="text-[0.625rem]">attached</Badge>
            </Field>
          ) : null}
          {hold.type === 'in_app_approval' && hold.has_device_key ? (
            <Field label="Device key">
              <Badge variant="info" className="text-[0.625rem]">bound</Badge>
            </Field>
          ) : null}
        </dl>

        {hold.fail_reason ? (
          <p className="flex items-start gap-2 rounded border border-warning/40 bg-warning/5 p-2 text-xs text-warning">
            <AlertTriangle size={12} aria-hidden className="mt-0.5 shrink-0" />
            <span>
              <span className="font-medium">Fail reason:</span> {hold.fail_reason}
            </span>
          </p>
        ) : null}

        <footer className="flex flex-wrap items-center justify-between gap-2 text-[0.6875rem] text-muted-fg">
          <span>
            Triggered by{' '}
            {hold.triggered_by === 'rule' && hold.triggered_by_rule_public_id ? (
              <Link
                to={`/admin/rules?preview=${hold.triggered_by_rule_public_id}`}
                className="font-mono text-fg hover:underline"
              >
                {hold.triggered_by_source}
              </Link>
            ) : (
              <>
                <span className="font-mono text-fg">{hold.triggered_by}</span>
                {hold.triggered_by_source ? <> · {hold.triggered_by_source}</> : null}
              </>
            )}
          </span>
          <code className="font-mono text-fg">decision #{hold.decision_id}</code>
        </footer>
      </div>
    </div>
  )
}

function FallbackAction({ isBlocking }: { isBlocking?: boolean }) {
  if (isBlocking) {
    return (
      <div className="flex items-start gap-3 rounded border border-status-failed/30 bg-status-failed/5 p-3 text-sm text-status-failed">
        <Ban size={16} aria-hidden className="mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">Hold + alert</p>
          <p className="mt-0.5 text-xs">
            The engine flagged this transaction with an alert and a hold; no
            customer-facing step-up was required.
          </p>
        </div>
      </div>
    )
  }
  return (
    <div className="flex items-start gap-3 rounded border border-border bg-muted/20 p-3 text-sm">
      <ShieldAlert size={16} aria-hidden className="mt-0.5 shrink-0 text-primary" />
      <div>
        <p className="font-semibold">Alert raised</p>
        <p className="mt-0.5 text-xs text-muted-fg">
          No step-up challenge was required for this evaluation. The engine
          raised an alert for analyst review.
        </p>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[0.5625rem] uppercase tracking-wider text-muted-fg">
        {label}
      </dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  )
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) return 'in the future'
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
